import { stopAgentStarts } from '#~/agent/provider/start-signal.js';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import { type ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../constants.js';

// In-tree navigation so Shutdown/Restart mount inside the existing
// BrowserRouter without a document reload. Assigning location.pathname
// reboots the SPA; a fast quit then closes the window before <Shutdown>
// can paint and ack. Per knowledge: electron-quit-path-discipline
// (ai.knowledge/platform) — the ack must come from an already-mounted
// renderer. Follows the in-router navigate() shape used by
// agent/components/monitoring/wiring.ts.

/** Registers Electron exit IPC and navigates in-tree. Renders nothing. */
export const ExitNavigator = (): ReactElement | null => {
  const navigate = useNavigate();

  useEffect(() => {
    const ipc = window.electron?.ipcRenderer;
    if (!ipc?.on) return undefined;

    const onShuttingDown = (): void => {
      if (isElectronTrayWindow()) return;
      stopAgentStarts();
      navigate('/' + Paths.agent.shutdown, { replace: true });
    };
    const onRestarting = (): void => {
      if (isElectronTrayWindow()) return;
      stopAgentStarts();
      navigate('/' + Paths.agent.restart, { replace: true });
    };

    ipc.on('window:shutting-down', onShuttingDown);
    ipc.on('window:restarting', onRestarting);
    return () => {
      ipc.removeListener?.('window:shutting-down', onShuttingDown);
      ipc.removeListener?.('window:restarting', onRestarting);
    };
  }, [navigate]);

  return null;
};
